import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { PaymentBalancingReport } from '../../../../domain/models/payment-report.model';
import { toCsv, downloadCsv } from '../report-export.util';

@Component({
  selector: 'app-balancing-tab',
  standalone: true,
  imports: [CommonModule, CurrencyPipe, MatTableModule, MatCardModule, MatButtonModule, MatIconModule],
  template: `
    @if (!report) {
      <p class="empty">Run a daily balance sheet here. Choose a business date above and press Run:
        the sheet reconciles that day's electronic settlements and cash drawers, and lists any
        payment that does not tie out.</p>
    }
    @if (report) {
      <div class="totals">
        <div class="stat"><span class="num">{{ report.totals.totalRecorded | currency }}</span><span class="lbl">Recorded</span></div>
        <div class="stat"><span class="num">{{ report.totals.totalSettled | currency }}</span><span class="lbl">Settled</span></div>
        <div class="stat"><span class="num">{{ report.totals.totalCashExpected | currency }}</span><span class="lbl">Cash expected</span></div>
        <div class="stat"><span class="num">{{ report.totals.totalCashDeclared | currency }}</span><span class="lbl">Cash declared</span></div>
        <div class="stat" [class.bad]="report.totals.netOverShort < 0">
          <span class="num">{{ report.totals.netOverShort | currency }}</span><span class="lbl">Net over/short</span></div>
      </div>

      <button mat-stroked-button class="csv-btn" (click)="exportCsv()"><mat-icon>table_view</mat-icon> Export CSV</button>

      <h3>Electronic (gateway settlement)</h3>
      <table mat-table [dataSource]="report.electronic" class="t">
        <ng-container matColumnDef="settlementStatus"><th mat-header-cell *matHeaderCellDef>Status</th><td mat-cell *matCellDef="let e">{{ e.settlementStatus }}</td></ng-container>
        <ng-container matColumnDef="count"><th mat-header-cell *matHeaderCellDef>Count</th><td mat-cell *matCellDef="let e">{{ e.count }}</td></ng-container>
        <ng-container matColumnDef="recordedAmount"><th mat-header-cell *matHeaderCellDef>Recorded</th><td mat-cell *matCellDef="let e">{{ e.recordedAmount | currency }}</td></ng-container>
        <ng-container matColumnDef="settledAmount"><th mat-header-cell *matHeaderCellDef>Settled</th><td mat-cell *matCellDef="let e">{{ e.settledAmount | currency }}</td></ng-container>
        <ng-container matColumnDef="variance"><th mat-header-cell *matHeaderCellDef>Variance</th><td mat-cell *matCellDef="let e">{{ e.variance | currency }}</td></ng-container>
        <tr mat-header-row *matHeaderRowDef="elecCols"></tr>
        <tr mat-row *matRowDef="let row; columns: elecCols;"></tr>
      </table>

      @if (report.electronicExceptions.length) {
        <h3>Exceptions</h3>
        <table mat-table [dataSource]="report.electronicExceptions" class="t">
          <ng-container matColumnDef="receiptNumber"><th mat-header-cell *matHeaderCellDef>Receipt</th><td mat-cell *matCellDef="let x">{{ x.receiptNumber }}</td></ng-container>
          <ng-container matColumnDef="methodType"><th mat-header-cell *matHeaderCellDef>Method</th><td mat-cell *matCellDef="let x">{{ x.methodType }}</td></ng-container>
          <ng-container matColumnDef="amount"><th mat-header-cell *matHeaderCellDef>Amount</th><td mat-cell *matCellDef="let x">{{ x.amount | currency }}</td></ng-container>
          <ng-container matColumnDef="reason"><th mat-header-cell *matHeaderCellDef>Reason</th><td mat-cell *matCellDef="let x">{{ x.reason }}</td></ng-container>
          <tr mat-header-row *matHeaderRowDef="excCols"></tr>
          <tr mat-row *matRowDef="let row; columns: excCols;"></tr>
        </table>
      }

      <h3>Cash drawers</h3>
      <table mat-table [dataSource]="report.cashDrawers" class="t">
        <ng-container matColumnDef="officeName"><th mat-header-cell *matHeaderCellDef>Office</th><td mat-cell *matCellDef="let d">{{ d.officeName }}</td></ng-container>
        <ng-container matColumnDef="cashierName"><th mat-header-cell *matHeaderCellDef>Cashier</th><td mat-cell *matCellDef="let d">{{ d.cashierName }}</td></ng-container>
        <ng-container matColumnDef="expectedCash"><th mat-header-cell *matHeaderCellDef>Expected</th><td mat-cell *matCellDef="let d">{{ d.expectedCash | currency }}</td></ng-container>
        <ng-container matColumnDef="declaredCash"><th mat-header-cell *matHeaderCellDef>Declared</th><td mat-cell *matCellDef="let d">{{ d.declaredCash | currency }}</td></ng-container>
        <ng-container matColumnDef="overShort"><th mat-header-cell *matHeaderCellDef>Over/Short</th><td mat-cell *matCellDef="let d" [class.bad]="d.overShort < 0">{{ d.overShort | currency }}</td></ng-container>
        <tr mat-header-row *matHeaderRowDef="drawerCols"></tr>
        <tr mat-row *matRowDef="let row; columns: drawerCols;"></tr>
      </table>
    }
  `,
  styles: [`
    .empty { color: #64748b; }
    .totals { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 12px; }
    .stat { display: flex; flex-direction: column; padding: 12px 18px; border: 1px solid #e2e8f0; border-radius: 10px; min-width: 120px; }
    .stat .num { font-size: 18px; font-weight: 700; }
    .stat .lbl { font-size: 12px; color: #64748b; text-transform: uppercase; }
    .stat.bad .num, td.bad { color: #b91c1c; }
    h3 { margin: 18px 0 6px; font-size: 14px; }
    .t { width: 100%; margin-bottom: 8px; }
    .csv-btn { margin-bottom: 8px; }
  `],
})
export class BalancingTabComponent {
  @Input() report: PaymentBalancingReport | null = null;

  readonly elecCols = ['settlementStatus', 'count', 'recordedAmount', 'settledAmount', 'variance'];
  readonly excCols = ['receiptNumber', 'methodType', 'amount', 'reason'];
  readonly drawerCols = ['officeName', 'cashierName', 'expectedCash', 'declaredCash', 'overShort'];

  exportCsv(): void {
    if (!this.report) return;
    const rows = this.report.cashDrawers.map(d => [
      d.officeName, d.cashierName, String(d.expectedCash), String(d.declaredCash), String(d.overShort),
    ]);
    downloadCsv('balancing-drawers.csv',
      toCsv(['Office', 'Cashier', 'Expected', 'Declared', 'OverShort'], rows));
  }
}
